import { useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, Send, Upload, FileText, ExternalLink, Loader2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { timeAgo } from "./shared";

export function NotesThread({ collection, itemId, notes = [], onUpdate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${collection}/${itemId}`, { text });
      onUpdate?.(data);
      setText("");
    } catch { toast.error("Failed to add note"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <MessageSquarePlus className="h-4 w-4" /> Notes ({notes.length})
      </div>
      <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
        {notes.length === 0 && <div className="rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">No notes yet.</div>}
        {[...notes].reverse().map((n, i) => (
          <div key={i} className="rounded-lg border border-border bg-card px-3 py-2">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">{n.author}</span><span>{timeAgo(n.at)}</span>
            </div>
            <div className="mt-1 text-sm">{n.text}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-end gap-2">
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); add(); } }}
          placeholder="Add a note…" className="min-h-[40px] resize-none" data-testid="note-input" />
        <Button onClick={add} disabled={busy || !text.trim()} className="h-10 w-10 shrink-0 rounded-lg p-0" data-testid="note-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export function FileUploader({ label = "Upload file", onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.({ path: data.path, filename: data.filename });
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]" data-testid={testid}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
      <input type="file" className="hidden" accept="image/*,application/pdf,.doc,.docx" onChange={upload} />
    </label>
  );
}

export function FileChip({ file }) {
  if (!file?.path) return null;
  return (
    <a href={fileUrl(file.path)} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-1.5 text-xs transition-colors hover:border-[hsl(var(--accent))]">
      <FileText className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
      <span className="max-w-[160px] truncate">{file.filename || "View file"}</span>
      <ExternalLink className="h-3 w-3 text-muted-foreground" />
    </a>
  );
}
